import type { ITransactionImport, TransactionImportType } from "@types";
import type { TransactionImportRejection } from "./importState";

type Translate = (id: string, values?: Record<string, string | number>) => string;

const REJECTION_KEYS: Record<string, string> = {
  IMPORT_FILE_REQUIRED: "transactionEntry.import.error.fileRequired",
  IMPORT_INVALID_TYPE: "transactionEntry.import.error.invalidType",
  IMPORT_INVALID_FORMAT: "transactionEntry.import.error.invalidFormat",
  IMPORT_FILE_TOO_LARGE: "transactionEntry.import.error.fileTooLarge",
  IMPORT_INVALID_TEMPLATE: "transactionEntry.import.error.invalidTemplate",
  IMPORT_EMPTY_FILE: "transactionEntry.import.error.emptyFile",
  IMPORT_NOT_FOUND: "transactionEntry.import.error.notFound",
  IMPORT_TRACKING_FAILED: "transactionEntry.import.error.trackingFailed",
  ACCESS_DENIED: "transactionEntry.import.error.accessDenied",
};

const STATUS_KEYS: Record<number, string> = {
  401: "transactionEntry.import.error.sessionExpired",
  403: "transactionEntry.import.error.accessDenied",
  413: "transactionEntry.import.error.fileTooLarge",
};

export const resolveImportRejectionMessage = (
  rejection: TransactionImportRejection | null | undefined,
  type: TransactionImportType,
  t: Translate
): string => {
  const code = rejection?.code;

  if (code === "IMPORT_WRONG_TEMPLATE") {
    return t(
      type === "RECEITA"
        ? "transactionEntry.import.error.wrongTemplate.revenue"
        : "transactionEntry.import.error.wrongTemplate.expense"
    );
  }

  if (code === "IMPORT_TOO_MANY_ROWS") return rejection?.detail || t("transactionEntry.import.error.tooManyRows");

  if (code && REJECTION_KEYS[code]) return t(REJECTION_KEYS[code]);

  if (rejection?.status && STATUS_KEYS[rejection.status]) return t(STATUS_KEYS[rejection.status]);

  return rejection?.detail || t("transactionEntry.import.error.generic");
};

export type ImportResultTone = "success" | "warning" | "danger";

export const resolveImportResultTone = (data: ITransactionImport): ImportResultTone => {
  if (data.status === "FAILED") return "danger";
  if (data.status === "COMPLETED_WITH_ERRORS" || (data.summary?.discardedRows ?? 0) > 0) return "warning";
  return "success";
};

export const resolveImportResultMessage = (data: ITransactionImport, t: Translate): string => {
  const imported = data.summary?.importedRows ?? 0;
  const discarded = data.summary?.discardedRows ?? 0;
  const tone = resolveImportResultTone(data);

  if (tone === "danger") return t("transactionEntry.import.result.failed");
  if (tone === "warning") return t("transactionEntry.import.result.completedWithErrors", { imported, discarded });
  return t("transactionEntry.import.result.completed", { imported });
};
